// ============================================================================
// TOURNAMENT TRAINING - challenge hub
// ============================================================================
// Its own section under Training: themed tournament challenges that pay PSS
// Coins on completion (server-validated, 1x playthrough). Tiered rewards -
// the harder the challenge, the bigger the payout.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Heading, Text, VStack, HStack, Grid, Badge, Spinner,
} from '@chakra-ui/react';
import { Trophy, ArrowLeft, Lock, CheckCircle, Coins, Target, BookOpen } from 'lucide-react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import gameApiService from '../services/gameApiService';
import { auth } from '../firebase';
import { createTrainingTable } from '../services/training/trainingTableService';
import {
  TOURNAMENT_CHALLENGES, TIER_META, TournamentChallenge, getSkillLabel, PRO_SERIES_BLURB,
} from '../services/training/tournamentChallenges';

const TournamentTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState<string | null>(null);

  // Live mini-game: spin up a real training table with the challenge's
  // asymmetric stacks and jump straight into the game.
  const launchSimulation = async (ch: TournamentChallenge) => {
    if (!ch.simulation || launching) return;
    const user = auth.currentUser;
    if (!user) return;
    setLaunching(ch.id);
    try {
      const sim = ch.simulation;
      const table = await createTrainingTable({
        tableName: ch.title,
        hostId: user.uid,
        hostName: user.displayName || user.email?.split('@')[0] || 'Player',
        smallBlind: sim.smallBlind,
        bigBlind: sim.bigBlind,
        startingStack: Math.max(1, Math.round(sim.botChips / sim.bigBlind)),
        botCount: sim.botCount,
        botDifficulty: sim.botDifficulty,
        // Co-op: up to 3 humans (host + 2 friends via invite code) + bots
        maxPlayers: sim.botCount + 3,
        autoStart: true,
        challenge: {
          id: ch.id,
          title: ch.title,
          heroChips: sim.heroChips,
          botChips: sim.botChips,
          goalType: sim.goalType,
          goalChips: sim.goalChips,
          ...(sim.goalCount !== undefined ? { goalCount: sim.goalCount } : {}),
          maxHands: sim.maxHands,
          rewardPkCash: ch.rewardPkCash,
          mission: sim.mission,
          tips: sim.tips,
          skill: getSkillLabel(ch.concept),
          concept: ch.concept,
        },
      });
      // NOTE: the game is NOT started here. The training page shows the
      // mission briefing first and starts dealing only when the player
      // clicks 'Deal Me In' - otherwise bots played hands in the background
      // while the briefing was being read.
      navigate(`/training/${table.tableId}`);
    } catch (err) {
      console.error('[TOURNAMENT_TRAINING] launch failed:', err);
    } finally {
      setLaunching(null);
    }
  };

  useEffect(() => {
    // The claims call decorates cards with COMPLETED badges - it must never
    // hold the whole hub behind the spinner. 4s cap, then render without.
    let done = false;
    const finish = (claimsData?: Record<string, any>) => {
      if (done) return;
      done = true;
      if (claimsData) setClaims(claimsData);
      setLoading(false);
    };
    const cap = setTimeout(() => finish(), 4000);
    (async () => {
      const r = await gameApiService.getTournamentChallengeState();
      clearTimeout(cap);
      finish(r.success ? (r.claims || {}) : undefined);
    })();
    return () => clearTimeout(cap);
  }, []);

  const tiers: Array<TournamentChallenge['tier']> = ['bronze', 'silver', 'gold', 'diamond'];

  return (
    <Box minH="100vh" bg="gray.900" pb={16}>
      <Container maxW="5xl" pt={6}>
        <HStack mb={2} justify="space-between">
          <PokerButton size="sm" variant="secondary" onClick={() => navigate('/training')}>
            <ArrowLeft size={14} style={{ marginRight: 4 }} /> Training
          </PokerButton>
          <PokerButton size="sm" variant="secondary"
            onClick={() => navigate('/training/glossary?category=tournament')}>
            <BookOpen size={14} style={{ marginRight: 4 }} /> Tournament Glossary
          </PokerButton>
        </HStack>

        <HStack mb={1}>
          <Trophy size={26} color={POKER_COLORS.gold} />
          <Heading size="lg" color="white">Tournament Training</Heading>
        </HStack>
        <Text color="gray.400" mb={6} fontSize="sm" maxW="640px">
          Master the skills that win tournaments: short-stack play, steals, ICM
          pressure, and final-table laddering. Pass a challenge to earn PSS
          Coins - harder tiers pay more. Rewards must be played through 3x before redemption.
        </Text>

        {loading ? (
          <Box textAlign="center" py={16}><Spinner color="cyan.400" size="lg" /></Box>
        ) : (() => {
          const liveChallenges = TOURNAMENT_CHALLENGES.filter(ch => ch.kind === 'simulation' && ch.simulation);
          const proSeries = TOURNAMENT_CHALLENGES.filter(ch => ch.kind === 'gauntlet' && ch.proSeries);
          const drills = TOURNAMENT_CHALLENGES.filter(ch => ch.kind === 'gauntlet' && !ch.proSeries);

          const specLine = (ch: TournamentChallenge): string => {
            const sim = ch.simulation!;
            const bb = sim.bigBlind;
            if (sim.goalType === 'preflopWins') {
              return `Win ${sim.goalCount} pots preflop in ${sim.maxHands} hands`;
            }
            if (sim.goalType === 'survive') {
              return `Hold ${Math.round(sim.goalChips / bb)}bb for ${sim.maxHands} hands`;
            }
            return `${Math.round(sim.heroChips / bb * 2) / 2}bb → ${Math.round(sim.goalChips / bb)}bb in ${sim.maxHands} hands`;
          };

          const renderCard = (ch: TournamentChallenge) => {
            const meta = TIER_META[ch.tier];
            const done = !!claims[ch.id];
            const isLive = ch.kind === 'simulation' && !!ch.simulation;
            return (
              <PokerCard key={ch.id}>
                <VStack align="stretch" spacing={3} p={1}>
                  <HStack justify="space-between" align="start">
                    <Box>
                      <HStack>
                        <Text color="white" fontWeight="bold" fontSize="md">{ch.title}</Text>
                        {ch.proSeries && (
                          <Badge bg="linear-gradient(135deg, #b91c1c, #f59e0b)" color="white" fontSize="8px"
                            px={2} borderRadius="full" letterSpacing="0.5px">
                            PRO SERIES
                          </Badge>
                        )}
                        {done && (
                          <Badge colorScheme="green" fontSize="9px" display="inline-flex" alignItems="center" gap="3px">
                            <CheckCircle size={10} /> COMPLETED · REWARD CLAIMED
                          </Badge>
                        )}
                      </HStack>
                      <HStack spacing={2} mt={0.5}>
                        <Badge bg={`${meta.color}33`} color={meta.color} fontSize="9px">{meta.label}</Badge>
                        <Text color={meta.color} fontSize="xs" fontWeight="semibold">{ch.tagline}</Text>
                      </HStack>
                    </Box>
                    <Badge
                      bg={`${POKER_COLORS.gold}22`} color={POKER_COLORS.gold}
                      px={2} py={1} borderRadius="md" fontSize="xs"
                    >
                      <HStack spacing={1}>
                        <Coins size={11} />
                        <Text>${ch.rewardPkCash.toFixed(2)}</Text>
                      </HStack>
                    </Badge>
                  </HStack>

                  <Text color="gray.400" fontSize="sm">{ch.description}</Text>

                  {/* The tournament skill this challenge trains */}
                  <HStack spacing={1}>
                    <Text fontSize="10px" color="gray.500" fontWeight="bold" letterSpacing="wide">
                      IMPROVES:
                    </Text>
                    <Text fontSize="10px" color={POKER_COLORS.cyan} fontWeight="semibold">
                      {getSkillLabel(ch.concept)}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    {isLive ? (
                      <HStack fontSize="xs" color="#f59e0b" spacing={1}>
                        <Target size={12} />
                        <Text fontWeight="bold">{specLine(ch)}</Text>
                      </HStack>
                    ) : (
                      <HStack fontSize="xs" color="gray.500" spacing={1}>
                        <Target size={12} />
                        <Text>{ch.steps.length} decisions · pass {ch.passThreshold}+</Text>
                      </HStack>
                    )}
                    {isLive ? (
                      <PokerButton
                        size="sm"
                        variant={done ? 'secondary' : undefined}
                        isLoading={launching === ch.id}
                        isDisabled={!!launching}
                        onClick={() => launchSimulation(ch)}
                      >
                        {done ? 'Play Again' : 'Play Live'}
                      </PokerButton>
                    ) : (
                      <HStack spacing={2}>
                        {/* Tutorial for the entry tiers: the coach walks the
                            winning line first - no reward, no clock. */}
                        {(ch.tier === 'bronze' || ch.tier === 'silver') && (
                          <PokerButton
                            size="sm"
                            variant="secondary"
                            onClick={() => navigate(`/training/tournament/${ch.id}/live?tutorial=1`)}
                          >
                            Tutorial
                          </PokerButton>
                        )}
                        <PokerButton
                          size="sm"
                          variant={done ? 'secondary' : undefined}
                          onClick={() => navigate(`/training/tournament/${ch.id}/live`)}
                        >
                          {done ? 'Play Again' : 'Play Live'}
                        </PokerButton>
                      </HStack>
                    )}
                  </HStack>
                </VStack>
              </PokerCard>
            );
          };

          return (
            <VStack align="stretch" spacing={10}>
              {/* ── LIVE CHALLENGES: the marquee - real tables, real bots ── */}
              <Box>
                <HStack mb={1}>
                  <Box w="10px" h="10px" borderRadius="full" bg="#f59e0b" />
                  <Text color="white" fontWeight="bold" letterSpacing="wide" fontSize="lg">
                    LIVE CHALLENGES
                  </Text>
                  <Badge colorScheme="orange" fontSize="9px">PLAY AT A REAL TABLE</Badge>
                </HStack>
                <Text color="gray.500" fontSize="xs" mb={4}>
                  Sit at a live table against bots with a handicapped stack and a
                  mission. Complete the mission to claim the reward.
                </Text>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {tiers.flatMap(tier => liveChallenges.filter(ch => ch.tier === tier)).map(renderCard)}
                </Grid>
              </Box>

              {/* ── PSS PRO SERIES: exclusive spots from a verified pro ── */}
              {proSeries.length > 0 && (
                <Box>
                  <HStack mb={1}>
                    <Box w="10px" h="10px" borderRadius="full"
                      bg="linear-gradient(135deg, #b91c1c, #f59e0b)" />
                    <Text color="white" fontWeight="bold" letterSpacing="wide" fontSize="lg">
                      PSS PRO SERIES
                    </Text>
                    <Badge bg="linear-gradient(135deg, #b91c1c, #f59e0b)" color="white" fontSize="9px"
                      px={2} borderRadius="full">EXCLUSIVE</Badge>
                  </HStack>
                  <Text color="gray.400" fontSize="xs" mb={4} maxW="640px">
                    {PRO_SERIES_BLURB}
                  </Text>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    {tiers.flatMap(tier => proSeries.filter(ch => ch.tier === tier)).map(renderCard)}
                  </Grid>
                </Box>
              )}

              {/* ── STRATEGY DRILLS: scenario gauntlets, played at the table ── */}
              <Box>
                <HStack mb={1}>
                  <Box w="10px" h="10px" borderRadius="full" bg={POKER_COLORS.cyan} />
                  <Text color="white" fontWeight="bold" letterSpacing="wide" fontSize="lg">
                    SCENARIO DRILLS
                  </Text>
                  <Badge colorScheme="cyan" fontSize="9px">EVERY SPOT PLAYED AT THE TABLE</Badge>
                </HStack>
                <Text color="gray.500" fontSize="xs" mb={4}>
                  Real tournament spots dealt to you at a live felt - your cards,
                  a ticking clock, real action buttons, and instant feedback after
                  every decision. Entry tiers include a Tutorial walkthrough that
                  shows the winning line first; rewards only pay on real runs.
                </Text>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                  {tiers.flatMap(tier => drills.filter(ch => ch.tier === tier)).map(renderCard)}
                </Grid>
              </Box>
            </VStack>
          );
        })()}
      </Container>
    </Box>
  );
};

export default TournamentTrainingPage;
